import { Trash2 } from 'lucide-react';
import { useState } from 'react';
import { createPrivacyErasureChallenge, erasePersonalData, type PrivacyChallenge } from '../../api/client';
import { Button } from '../../components/Button';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { ScreenHeader } from '../../components/ScreenHeader';
import { useI18n } from '../../i18n/I18nProvider';
import { clearLocalPhotos } from '../../storage/photos';
import { clearStoredResults, type StorageScope } from '../../storage/results';

export function PrivacyPanel({ scope, onErased }: { scope?: StorageScope | null; onErased?: () => void }) {
  const { t } = useI18n();
  const [step, setStep] = useState<0 | 1 | 2>(0);
  const [challenge, setChallenge] = useState<PrivacyChallenge | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const continueDeletion = async () => {
    setBusy(true); setError('');
    try { setChallenge(await createPrivacyErasureChallenge()); setStep(2); }
    catch (cause) { setError(cause instanceof Error ? cause.message : t('privacy.error')); setStep(0); }
    finally { setBusy(false); }
  };
  const erase = async () => {
    if (!challenge) return;
    setBusy(true); setError('');
    try {
      const result = await erasePersonalData(challenge);
      if (scope) await clearStoredResults(scope);
      await clearLocalPhotos();
      sessionStorage.removeItem('walldCurrentPhotoId');
      setSuccess(`${t('privacy.success')}${result.preservedAccess ? ` ${t('privacy.successAccess')}` : ''}`);
      setChallenge(null); setStep(0); onErased?.();
    } catch (cause) { setError(cause instanceof Error ? cause.message : t('privacy.error')); setStep(0); }
    finally { setBusy(false); }
  };
  return <section className="privacy-panel">
    <div className="privacy-panel__heading"><h2>{t('privacy.title')}</h2><p>{t('privacy.copy')}</p><p className="privacy-role-note">{t('privacy.roleNote')}</p></div>
    {error && <p className="inline-error" role="alert">{error}</p>}{success && <p className="inline-success" role="status">{success}</p>}
    <Button type="button" variant="danger" block icon={<Trash2 />} onClick={() => { setError(''); setSuccess(''); setStep(1); }}>{t('privacy.erase')}</Button>
    <ConfirmDialog open={step === 1} title={t('privacy.firstTitle')} copy={t('privacy.firstCopy')} confirmLabel={t('privacy.continue')} cancelLabel={t('common.cancel')} busy={busy} onCancel={() => setStep(0)} onConfirm={() => void continueDeletion()} />
    <ConfirmDialog open={step === 2} title={t('privacy.finalTitle')} copy={t('privacy.finalCopy')} confirmLabel={t('privacy.confirm')} cancelLabel={t('common.cancel')} busy={busy} onCancel={() => { setStep(0); setChallenge(null); }} onConfirm={() => void erase()} />
  </section>;
}

export function PrivacyScreen({ scope, onBack, onErased }: { scope?: StorageScope | null; onBack: () => void; onErased?: () => void }) {
  const { t } = useI18n();
  return <main className="screen"><ScreenHeader title={t('privacy.title')} onBack={onBack} /><div className="screen-content privacy-screen"><PrivacyPanel scope={scope} onErased={onErased} /></div></main>;
}
